import {PaginatedResultModel} from '../../../models/paginated-result.model'
import {UsersModel} from '../../../models/user.model'
import {ListQueryParams, ApiResponse} from '../ApiClient'
import {GetAPIDomain, GetAPIHeaders, GetApiHeadersFormData, GetAPIHeadersPatch} from '../Helper'

export class UserQueryParams extends ListQueryParams {
  WithShowSuspended(value: any) {
    if (!value) this.WithParam('status', 'active')
    return this
  }

  WithRoleId(value: any) {
    this.WithParam('roleId', value)
    return this
  }

  WithStatus(value: any) {
    this.WithParam('status', value)
    return this
  }

  WithTeamId(value: any) {
    this.WithParam('teamId', value)
    return this
  }

  WithSiteId(value: any) {
    this.WithParam('siteId', value)
    return this
  }

  WithReportsToId(value: any) {
    this.WithParam('reportsToId', value)
    return this
  }
  WithSearchableOnly(value: any) {
    this.WithParam('searchableOnly', value)
    return this
  }
  WithUserTypeId(value: any) {
    this.WithParam('userTypeId', value)
    return this
  }
  WithBusinessUnitId(value: any) {
    this.WithParam('businessUnitId', value)
    return this
  }
  WithConnectId(value: any) {
    this.WithParam('connectId', value)
    return this
  }
  WithSuspended(value: boolean) {
    this.WithParam('suspended', value)
    return this
  }
  WithIncludeExcludedFromTeamView(value: boolean) {
    this.WithParam('includeExcludedFromTeamView', value)
    return this
  }
  WithExcludeHasAccountThemeUsers(value: boolean) {
    this.WithParam('excludeHasAccountThemeUsers', value)
    return this
  }
  WithExcludeHasLocalizationUsers(value: boolean) {
    this.WithParam('excludeHasLocalizationUsers', value)
    return this
  }
  WithNonExpiredOnly(value: boolean) {
    this.WithParam('nonExpiredOnly', value)
    return this
  }
  WithForExport(value: boolean) {
    this.WithParam('forExport', value)
    return this
  }
  WithIncludeTeam(value: boolean) {
    this.WithParam('includeTeam', value)
    return this
  }
}

export class UsersClient {
  static async GetActiveUserCount() {
    let query = (await GetAPIDomain()) + '/users/active'

    return await ApiResponse.Create(() =>
      fetch(query, {
        headers: GetAPIHeaders(),
        credentials: 'include',
      })
    )
  }

  static async ExportActiveUsers() {
    let query = (await GetAPIDomain()) + '/users/active/csv'

    let response = await fetch(query, {
      headers: GetAPIHeaders('text/csv'),
      credentials: 'include',
    })

    return await response.blob()
  }

  static async ExportUsers(params: UserQueryParams) {
    let query =
      (await GetAPIDomain()) + '/users/export/csv' + params.GenerateQueryStringWithoutPagination()

    let response = await fetch(query, {
      headers: GetAPIHeaders('text/csv'),
      credentials: 'include',
    })

    return await response.blob()
  }

  static async List(params: UserQueryParams) {
    let query = (await GetAPIDomain()) + '/users' + params.GenerateQueryString()

    return await ApiResponse.Create<PaginatedResultModel<UsersModel>>(() =>
      fetch(query, {
        headers: GetAPIHeaders(),
        credentials: 'include',
      })
    )
  }

  static async LookUp(params: UserQueryParams) {
    var query = (await GetAPIDomain()) + '/users/lookup' + params.GenerateQueryString()

    return await ApiResponse.Create(() =>
      fetch(query, {
        headers: GetAPIHeaders(),
        credentials: 'include',
      })
    )
  }

  static async Get(id: string) {
    var apiDomain = await GetAPIDomain()

    return await ApiResponse.Create(() =>
      fetch(apiDomain + '/users/' + id + '?excludeFavorites=true', {
        headers: GetAPIHeaders(),
        credentials: 'include',
      })
    )
  }

  static async GetTeamMemberships(id: string) {
    var apiDomain = await GetAPIDomain()

    return await ApiResponse.Create(() =>
      fetch(apiDomain + '/users/' + id + '/team-memberships', {
        headers: GetAPIHeaders(),
        credentials: 'include',
      })
    )
  }

  static async GetCurrentUser(email: string) {
    var apiDomain = await GetAPIDomain()
    return await ApiResponse.Create(() =>
      fetch(apiDomain + '/users/current/' + email, {
        headers: GetAPIHeaders(),
        credentials: 'include',
      })
    )
  }

  static async AddReportsToUsers(id: string, userIds: string[]) {
    var apiDomain = await GetAPIDomain()
    return await ApiResponse.Create(() =>
      fetch(apiDomain + '/users/' + id + '/reports-to-users', {
        method: 'POST',
        headers: GetAPIHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          userIds: userIds,
        }),
      })
    )
  }

  static async AddSkillsToUsers(id: string, skillIds: string[]) {
    var apiDomain = await GetAPIDomain()
    return await ApiResponse.Create(() =>
      fetch(apiDomain + '/users/' + id + '/skills', {
        method: 'POST',
        headers: GetAPIHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          skillIds,
        }),
      })
    )
  }

  static async SetRole(id: string, roleId: any) {
    var query = (await GetAPIDomain()) + '/users/' + id + '/set-roles'

    return await ApiResponse.Create(() =>
      fetch(query, {
        headers: GetAPIHeaders(),
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({
          roleIds: roleId,
        }),
      })
    )
  }

  static async SetTeam(
    userId: string,
    teamId: string,
    isPrimary: boolean = false,
    isTeamLeader: boolean = false
  ) {
    var query = (await GetAPIDomain()) + '/users/' + userId + '/set-team'

    let formData = new FormData()
    formData.append('userId', userId)
    formData.append('teamId', teamId)
    formData.append('isPrimary', isPrimary ? 'true' : 'false')
    formData.append('isTeamLeader', isTeamLeader ? 'true' : 'false')

    return await ApiResponse.Create(() =>
      fetch(query, {
        method: 'POST',
        headers: GetApiHeadersFormData(),
        credentials: 'include',
        body: formData,
      })
    )
  }

  static async RemoveTeam(userId: string, teamId: string) {
    var query = (await GetAPIDomain()) + '/users/' + userId + '/remove-team'

    let formData = new FormData()
    formData.append('userId', userId)
    formData.append('teamId', teamId)

    return await ApiResponse.Create(() =>
      fetch(query, {
        headers: GetApiHeadersFormData(),
        credentials: 'include',
        method: 'DELETE',
        body: formData,
      })
    )
  }

  static async Suspend(id: string) {
    var query = (await GetAPIDomain()) + '/users/' + id + '/suspend'

    return await ApiResponse.Create(() =>
      fetch(query, {
        headers: GetAPIHeaders(),
        method: 'POST',
        credentials: 'include',
      })
    )
  }
  static async Restore(id: string) {
    var query = (await GetAPIDomain()) + '/users/' + id + '/restore'

    return await ApiResponse.Create(() =>
      fetch(query, {
        headers: GetAPIHeaders(),
        method: 'POST',
        credentials: 'include',
      })
    )
  }

  static async Delete(id: string) {
    var query = (await GetAPIDomain()) + '/users/' + id

    return await ApiResponse.Create(() =>
      fetch(query, {
        headers: GetAPIHeaders(),
        method: 'DELETE',
        credentials: 'include',
      })
    )
  }

  static async Update(
    id: string,
    name: string,
    surname: string,
    email: string,
    mobile?: string,
    siteId?: string,
    hasCrmAccess?: boolean,
    hasMobileAccess?: boolean,
    reportsToUserId?: string,
    businessUnitId?: string,
    userTypeId?: string,
    searchable?: boolean,
    suspended?: boolean,
    isGuest?: boolean,
    accessExpiry?: string | null,
    connectId?: string | null,
    connectOverride?: boolean,
    parkingAvailability?: string | null,
    parkingAvailabilityExpiry?: string | null,
    bookOverride?: boolean,
    excludeOnMyTeamView?: boolean,
    managerEmployeeId?: string | null
  ) {
    let apiDomain = await GetAPIDomain()
    return await ApiResponse.Create(() =>
      fetch(apiDomain + '/users/' + id, {
        method: 'PATCH',
        headers: GetAPIHeadersPatch(),
        credentials: 'include',
        body: JSON.stringify([
          {
            op: 'replace',
            path: 'name',
            value: name,
          },
          {
            op: 'replace',
            path: 'surname',
            value: surname,
          },
          {
            op: 'replace',
            path: 'email',
            value: email,
          },
          {
            op: 'replace',
            path: 'mobile',
            value: mobile,
          },
          {
            op: 'replace',
            path: 'siteId',
            value: siteId,
          },
          {
            op: 'replace',
            path: 'hasCrmAccess',
            value: hasCrmAccess,
          },
          {
            op: 'replace',
            path: 'hasMobileAccess',
            value: hasMobileAccess,
          },
          {
            op: 'replace',
            path: 'reportsToUserId',
            value: reportsToUserId,
          },
          {
            op: 'replace',
            path: 'businessUnitId',
            value: businessUnitId,
          },
          {
            op: 'replace',
            path: 'userTypeId',
            value: userTypeId,
          },
          {
            op: 'replace',
            path: 'searchable',
            value: searchable,
          },
          {
            op: 'replace',
            path: 'suspended',
            value: suspended,
          },
          {
            op: 'replace',
            path: 'isGuest',
            value: isGuest,
          },
          {
            op: 'replace',
            path: 'accessExpiry',
            value: accessExpiry,
          },
          {
            op: 'replace',
            path: 'connectId',
            value: connectId,
          },
          {
            op: 'replace',
            path: 'connectOverride',
            value: connectOverride,
          },
          {
            op: 'replace',
            path: 'parkingAvailability',
            value: parkingAvailability,
          },
          {
            op: 'replace',
            path: 'parkingAvailabilityExpiry',
            value: parkingAvailabilityExpiry,
          },
          {
            op: 'replace',
            path: 'bookOverride',
            value: bookOverride,
          },
          {
            op: 'replace',
            path: 'excludeOnMyTeamView',
            value: excludeOnMyTeamView,
          },
          {
            op: 'replace',
            path: 'managerEmployeeId',
            value: managerEmployeeId,
          },
        ]),
      })
    )
  }

  static async UploadPhoto(userId: string, file: any) {
    let formData = new FormData()
    formData.append('userId', userId)
    formData.append('file', file)

    var accessToken = localStorage.getItem('acess-token')

    const response = await fetch((await GetAPIDomain()) + '/users/upload-photo', {
      method: 'POST',
      credentials: 'include',
      headers: new Headers({Authorization: 'Bearer ' + accessToken}),
      body: formData,
    })

    if (!response.ok) {
      return false
    }

    const data = await response.json()
  }

  static async DeletePhoto(id: string) {
    var query = (await GetAPIDomain()) + '/users/' + id + '/remove-photo'

    return await ApiResponse.Create(() =>
      fetch(query, {
        headers: GetAPIHeaders(),
        method: 'DELETE',
        credentials: 'include',
      })
    )
  }

  static async GenerateTwoFactorAuth(email: string) {
    var apiDomain = await GetAPIDomain()
    return await ApiResponse.Create(() =>
      fetch(apiDomain + '/users/generate-two-factor-auth', {
        method: 'POST',
        credentials: 'include',
        headers: GetAPIHeaders(),
        body: JSON.stringify({
          email: email,
        }),
      })
    )
  }

  static async CheckTwoFactorAuth(email: string, token: string) {
    var apiDomain = await GetAPIDomain()
    return await ApiResponse.Create(() =>
      fetch(apiDomain + '/users/check-two-factor-auth', {
        method: 'POST',
        headers: GetAPIHeaders(),
        body: JSON.stringify({
          email: email,
          token: token,
        }),
      })
    )
  }

  //BEGIN PASSWORD RESET
  // accountId scopes the email lookup to one account. Without it the API takes whichever user
  // has that email first, which is the wrong one when the address exists in more than one account.
  static async BeginPasswordReset(email: string, accountId?: string | null) {
    const headers = new Headers({
      'content-type': 'application/json',
      'x-account-discovery': 'x-account-discovery',
    })
    if (accountId) headers.append('x-account-id', accountId)

    const response = await fetch((await GetAPIDomain()) + '/reset-password/begin', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        email: email,
      }),
    })

    // Accounts without automatic tenant discovery get a 202 with no body back, which
    // response.json() can't parse. The reset has still been started.
    const text = await response.text()
    const data = text ? JSON.parse(text) : {}

    return {...data, successful: response.ok && data.successful !== false}
  }

  //COMPLETE PASSWORD RESET
  static async CompletePasswordReset(code: string, password: string) {
    const response = await fetch((await GetAPIDomain()) + '/reset-password/complete', {
      method: 'POST',
      headers: new Headers({
        'content-type': 'application/json',
        'x-account-discovery': 'x-account-discovery',
      }),
      body: JSON.stringify({
        code: code,
        password: password,
      }),
    })

    return await response.json()
  }

  static async GetSummary(id: string, month: number, year: number) {
    var apiDomain = await GetAPIDomain()
    return await ApiResponse.Create(() =>
      fetch(apiDomain + '/users/monthly-summary' + `?id=${id}&month=${month}&year=${year}`, {
        headers: GetAPIHeaders(),
        credentials: 'include',
      })
    )
  }
}
